'use client'

import { Tabs as TabsMain, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"

/**
 * Renders a tabs component.yadaj
 * 
 * @param {Object} props - The component props.
 * @param {Array} props.tabs - Array of tab objects with label and content.
 * @param {string} [props.defaultValue] - The default active tab value.
 * @param {string} [props.className] - The additional CSS class name for tabs container.
 * @param {string} [props.listClassName] - The additional CSS class name for tabs list.
 * @param {string} [props.triggerClassName] - The additional CSS class name for tab triggers.
 * @param {string} [props.contentClassName] - The additional CSS class name for tab content.
 * @param {Function} [props.onValueChange] - Callback when tab changes.
 * @param {Object} [props] - Additional props to be passed to the tabs.
 * @return {JSX.Element} The tabs component.
 */

interface Tab {
  label: string | React.ReactNode
  value: string
  content: React.ReactNode
  disabled?: boolean
  icon?: React.ReactNode
}

interface TabsProps {
  tabs: Tab[]
  defaultValue?: string
  className?: string
  listClassName?: string
  triggerClassName?: string
  contentClassName?: string
  onValueChange?: (value: string) => void
  [key: string]: any
}

export default function Tabs({ tabs, defaultValue, className, listClassName, triggerClassName, contentClassName, onValueChange, ...props }: TabsProps) {
  const firstTabValue = tabs[0]?.value || ''
  const activeDefaultValue = defaultValue || firstTabValue

  return (
    <TabsMain defaultValue={activeDefaultValue} onValueChange={onValueChange} className={className} {...props}>
      <TabsList className={listClassName}>
        {tabs.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value} disabled={tab.disabled} className={triggerClassName}>
            {tab.icon && <span>{tab.icon}</span>}
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {tabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value} className={contentClassName}>
          {tab.content}
        </TabsContent>
      ))}
    </TabsMain>
  )
}